import { AfterViewInit, Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GroupsService } from '../groups.service';
import {
  BaselineProgress,
  DailyProgress,
  Group,
  UserProgress,
} from 'src/app/shared/models/interfaces';
import { MatCardModule } from '@angular/material/card';
import { ClipboardModule } from 'ngx-clipboard';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/ui-components/confirm-dialog/confirm-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import { MatMenuModule } from '@angular/material/menu';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DATE_FORMATS } from 'src/app/shared/constants/date-formats';

@Component({
  selector: 'app-group-details',
  imports: [
    MatCardModule,
    ClipboardModule,
    MatIconModule,
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatDialogModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    UIComponentsModule,
    MatMenuModule,
  ],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS }],
  templateUrl: './group-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './group-details.component.scss',
})
export class GroupDetailsComponent implements OnInit, AfterViewInit {
  dataSource = new MatTableDataSource<UserProgress>();
  selectedGroup!: Group;
  users: UserProgress[] = [];
  admins: UserProgress[] = [];
  participants: UserProgress[] = [];
  displayedColumns: string[] = [
    'firstName',
    'lastName',
    'email',
    'statusDailyLogs',
    'statusGeneralLogs',
    'actions',
  ];
  isLoading = true;
  error: string | null = null;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    public groupsService: GroupsService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private snackbar: SnackbarService
  ) {}

  ngOnInit(): void {
    const groupId = Number(this.route.snapshot.paramMap.get('id'));

    this.groupsService.getGroupUserProgressReport(groupId).subscribe({
      error: (error) => {
        console.error('Error fetching user progress report:', error);
        this.snackbar.showErrorMessage('Failed to load user progress report.');
      },
    });

    this.groupsService.getGroupDetailById(groupId).subscribe({
      next: (group) => {
        this.selectedGroup = group || ({} as Group);
      },
      error: (error) => {
        const msg =
          error.status === 403
            ? 'You do not have permission to view this group.'
            : 'Failed to load group details.';
        this.snackbar.showErrorMessage(msg);
        this.isLoading = false;
      },
    });

    this.groupsService.getGroupParticipants(groupId).subscribe({
      next: (users) => {
        this.users = users || [];
        this.admins = this.filterGroupAdmins(this.users);
        this.participants = this.filterGroupParticipants(this.users);
        this.isLoading = false;
        this.updateDataSource();
      },
      error: (error) => {
        const msg =
          error.status === 403
            ? 'You do not have permission to view this group.'
            : 'Unexpected error occurred while loading participants.';
        this.snackbar.showErrorMessage(msg);
        this.error = msg;
        this.isLoading = false;
      },
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  updateDataSource() {
    this.dataSource.data = this.participants;
  }

  updatePaginator() {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
  }

  changeAdminRights(student: UserProgress, getsAdminRights: boolean) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Change Admin Rights for ${student.email}`,
        message: getsAdminRights
          ? 'Are you sure you want to grant admin rights to this user?'
          : 'Are you sure you want to remove admin rights from this user?',
        confirmButtonText: getsAdminRights ? 'Grant Admin' : 'Remove Admin',
        cancelButtonText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading = true;

        this.groupsService
          .updateAdminRights(this.selectedGroup.id, student.id, getsAdminRights)
          .subscribe({
            next: () => {
              // Update local user object
              student.isAdmin = getsAdminRights;

              // Update admins and participants list
              this.admins = this.filterGroupAdmins(this.users);
              this.participants = this.filterGroupParticipants(this.users);
              this.dataSource.data = this.participants;

              this.snackbar.showSuccessMessage(
                `${getsAdminRights ? 'Granted' : 'Removed'} admin rights for "${
                  student.email
                }".`
              );
              this.isLoading = false;
            },

            error: (err) => {
              console.error('Error changing admin rights:', err);

              if (err.status === 403) {
                this.snackbar.showErrorMessage(
                  'You do not have permission to change admin rights. You may not be an admin of this group.'
                );
                this.error =
                  'You do not have permission to change admin rights.';
              } else if (
                err.error.message.includes('User is not a group admin')
              ) {
                this.snackbar.showErrorMessage(
                  'You cannot change admin rights for a user who is not a group admin.'
                );
                this.error =
                  'You cannot change admin rights for a user who is not a group admin.';
              } else {
                this.error = 'An error occurred while changing admin rights.';
              }
              this.snackbar.showErrorMessage(this.error);
              this.isLoading = false;
            },
          });
      }
    });
  }

  removestudentFromGroup(student: UserProgress) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Are you sure you want to remove ${student.email} from the group?`,
        message:
          'This participant will lose access to the group and their progress will no longer be visible for the group Admins.',
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.groupsService
          .removeStudentFromGroup(this.selectedGroup.id, student.id)
          .subscribe({
            next: () => {
              this.participants = this.participants.filter(
                (s) => s.id !== student.id
              );
              this.dataSource.data = this.participants;

              this.snackbar.showSuccessMessage(
                `Student "${student.email}" removed successfully.`
              );
              this.isLoading = false;
            },
            error: (err) => {
              if (err.status === 403) {
                this.error =
                  'You do not have permission to remove students from this group.';
              } else {
                this.snackbar.showErrorMessage(
                  'An error occurred while removing the student.'
                );
                this.error = 'An error occurred while removing the student.';
              }
              this.snackbar.showErrorMessage(this.error);
              this.isLoading = false;
            },
          });
      }
    });
  }

  deleteGroup(group: Group) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: `Are you sure you want to delete the group ${group.groupName}?`,
        message:
          'This will permanently remove the group and all associated data. This action cannot be undone.',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.groupsService.deleteGroup(group.id).subscribe({
          next: () => {
            this.dataSource.data = this.dataSource.data.filter(
              (g) => g.id !== group.id
            );
            // Navigate to group-overview component after deletion
            this.groupsService.clearSelectedGroup();
            this.router.navigate(['/group']);

            // Display snackbar after successful deletion
            this.snackbar.showSuccessMessage(
              `Group "${group.groupName}" deleted successfully.`
            );
          },
          error: (err) => {
            console.error('Error deleting group:', err);

            if (err.status === 403) {
              this.snackbar.showErrorMessage(
                'You do not have permission to delete this group. You may not be an admin of this group.'
              );
            } else {
              this.snackbar.showErrorMessage(
                'An error occurred while deleting the group.'
              );
            }
          },
        });
      }
    });
  }

  filterGroupParticipants(users: UserProgress[]): UserProgress[] {
    return users.filter((user) => !user.isAdmin);
  }

  filterGroupAdmins(users: UserProgress[]): UserProgress[] {
    return users.filter((user) => user.isAdmin);
  }

  displayDailyProgress(participantsDailyProgress: DailyProgress[]): string {
    const usersCompletedDays = participantsDailyProgress
      ? participantsDailyProgress.filter(
          (progress) => progress.entriesCompleted === true
        ).length
      : 0;

    const start = new Date(this.selectedGroup.trackingPeriodStart);
    const finish = new Date(this.selectedGroup.trackingPeriodFinish);

    const timeDiff = finish.getTime() - start.getTime();
    const totalDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    return `${usersCompletedDays} / ${totalDays} days completed`;
  }

  displayBaselineProgress(baselineProgress: BaselineProgress[]): string {
    if (!baselineProgress) {
      return 'No baseline progress available';
    }
    const progress = baselineProgress[0];

    if (!progress) {
      return 'No baseline progress available';
    }
    return `${progress.baselineAnswered || 0} / ${
      progress.totalPossible || 'the total possible'
    } questions completed`;
  }

  inspectUserEmissions(user: UserProgress) {
    this.router.navigate([
      '/group',
      this.selectedGroup.id,
      'user',
      user.id,
      'report',
    ]);
  }
}
